import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { SubscribersSubNav } from '@/components/admin/SubscribersSubNav';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { adminEmailTemplatesService } from '@/services/admin-email-templates.service';
import { ApiError } from '@/services/http';
import type { EmailTemplateView } from '@/types/api';

export function AdminEmailTemplatesPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EmailTemplateView[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState<EmailTemplateView | null>(null);

  const reload = () => {
    setLoading(true);
    adminEmailTemplatesService
      .list()
      .then(setTemplates)
      .catch((err) =>
        toast.error(err instanceof ApiError ? err.message : 'Could not load templates.'),
      )
      .finally(() => setLoading(false));
  };

  useEffect(reload, []);

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      await adminEmailTemplatesService.remove(target.id);
      setTemplates((prev) => prev.filter((t) => t.id !== target.id));
      toast.success('Template deleted.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete template.');
    }
  };

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />
        <SubscribersSubNav />

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="or-kicker text-or-orange-600">Admin · Subscribers</div>
            <h1 className="mt-1 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
              Email templates
            </h1>
            <p className="mt-1 max-w-xl text-[13px] text-or-slate-600">
              Reusable HTML or plain-text emails. Send one to a list from the Broadcasts tab.
            </p>
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={() => navigate('/admin/subscribers/templates/new')}
          >
            <Plus className="h-4 w-4" aria-hidden />
            New template
          </Button>
        </div>

        <div className="overflow-hidden rounded-2xl border border-or-slate-200 bg-white">
          {loading && templates.length === 0 ? (
            <div className="px-4 py-12 text-center text-or-slate-500">Loading…</div>
          ) : templates.length === 0 ? (
            <div className="px-4 py-12 text-center text-or-slate-500">No templates yet.</div>
          ) : (
            <ul>
              {templates.map((tpl) => (
                <li
                  key={tpl.id}
                  className="flex items-center justify-between border-t border-or-slate-100 px-4 py-3 first:border-t-0"
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/subscribers/templates/${tpl.id}`)}
                    className="flex-1 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-or-slate-900">{tpl.name}</span>
                      <Chip variant="default">{tpl.format.toUpperCase()}</Chip>
                    </div>
                    <p className="text-[12px] text-or-slate-500">{tpl.subject}</p>
                  </button>
                  <div className="flex items-center gap-3">
                    <span className="text-[12px] text-or-slate-400">
                      {new Date(tpl.updatedAt).toLocaleDateString()}
                    </span>
                    <Button variant="danger" size="sm" onClick={() => setPendingDelete(tpl)}>
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDelete(null);
        }}
        title="Delete this template?"
        description={
          pendingDelete ? `"${pendingDelete.name}" will be permanently deleted.` : ''
        }
        confirmLabel="Delete template"
        confirmVariant="danger"
        onConfirm={handleDelete}
      />
    </AppShell>
  );
}
